import React, { ReactNode, useEffect, useState } from 'react'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'

import MyProfileGroupHra from 'employeeDashboard/common/employeeProfile'
import MyProfilePerks from 'employeeDashboard/perks/employeeProfile'

import { Feature, NotificationItem, getNotifications, saveCompanyUserNotificationAction } from 'core'
import { User } from 'core/application/account/loginUser'
import { Permissions } from 'core/application/account/permissions'
import updateReimbursementProduct from 'core/application/adminDashboard/benefitDetails/design/qsehra/productClass/updateReimbursementProduct'
import getShowPopupForPremiumPlus, {
  PremiumPlusQueryResponse,
} from 'core/application/adminDashboard/benefitDetails/getShowPopupForPremiumPlus'
import { NotificationType } from 'core/application/adminDashboard/notifications/getNotifications'
import { ProductIds } from 'core/application/commons/config'

import { useLoginUserV2 } from 'common/hooks/useLoginUser'

import { useCommandRequestV2, useFeatureFlag } from 'common/hooks'

import NotFound from 'errorPages/NotFound'

import ChangePassword from '../account/changePassword'
import { Notification } from '../common/components'
import useAdminTasks from '../common/hooks/useAdminTasks'
import useCommandRequest from '../common/hooks/useCommandRequest'
import useCurrentProduct from '../common/hooks/useCurrentProduct'
import useQueryRequestV2 from '../common/hooks/useQueryRequestV2'
import { ROUTE_PATHS } from '../common/routePaths'
import AdminDashboardWrapper from './AdminDashboardWrapper'
import EnhanceBenefitModal from './EnhanceBenefitModal'
import SelectReimbursementProductModal from './SelectReimbursementProductModal'
import GchraRenewalTasks from './home/groupHra/renewalTasks'
import IchraRenewalTasks from './home/ichra/renewalTasks'
import AssignEmployeeClasses from './home/ichra/renewalTasks/assignEmployeeClasses'
import PerksRenewalTasks from './home/perks/renewalTasks'
import AssignPerksEmployeeClasses from './home/perks/renewalTasks/assignEmployeeClasses'
import QsehraRenewalTasks from './home/qsehra/renewalTasks'
import { MenuItem, getNavigationMenu } from './navigationMenu'

type AdminDashboardProps = {}

const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const location = useLocation()
  const history = useHistory()
  const product = useCurrentProduct()

  const [tasks, isTasksLoading, reloadTasks] = useAdminTasks([], false)
  const { user } = useLoginUserV2()
  const showSmsMessages = useFeatureFlag(Feature.SmsTracking)

  const navigationMenu = getNavigationMenu(product.productId, tasks, showSmsMessages)
  const [showSelectReimbursementProductModal, setShowSelectReimbursementProductModal] = useState<boolean>(false)
  const [showEnhanceBenefitModal, setShowEnhanceBenefitModal] = useState<boolean>(true)

  const [sendCommand] = useCommandRequest()

  const {
    data: showPremiumPlusModals,
    isLoading: isShowPremiumModalsLoading,
    reload: reloadShowPremiumPlus,
  } = useQueryRequestV2<PremiumPlusQueryResponse>({
    queryFn: () => getShowPopupForPremiumPlus(),
  })

  const { sendCommand: sendSaveCompanyUserNotificationAction, isLoading: isSaveCompanyUserNotificationActionLoading } =
    useCommandRequestV2((notificationId: string) => saveCompanyUserNotificationAction(notificationId))

  const { data: notifications, sendQuery: sendGetNotifications } = useQueryRequestV2<NotificationItem[]>({
    initialState: null,
    queryFn: () => getNotifications(),
  })

  useEffect(() => {
    if (!userIsAllowedToEnterDashboard(user)) {
      history.push(ROUTE_PATHS.LOGIN_PATH)
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      await reloadTasks()
    })()
  }, [location])

  useEffect(() => {
    if (location.pathname === '' || location.pathname === '/') {
      history.push(ROUTE_PATHS.DASHBOARD_PATH)
    }
  }, [])

  const getChildrenRoutes = (children: MenuItem[]): ReactNode[] | null[] =>
    children.map((child: MenuItem) =>
      child.children.length > 0
        ? child.children.map((nestedChild) => getRoute(nestedChild))
        : !child.disabled
        ? getRoute(child)
        : null,
    )

  const getRoute = (item: MenuItem): ReactNode => {
    return <Route key={item.key} path={item.path} render={(props) => <item.component name={item.key} {...props} />} />
  }

  const userIsAllowedToEnterDashboard = (user: User): boolean => {
    return user !== null && user.permissions.includes(Permissions.AccessAdminDashboard)
  }

  const resolveRenewalTasks = (() => {
    switch (product.productId) {
      case ProductIds.GroupHraId:
        return GchraRenewalTasks
      case ProductIds.QsehraId:
        return QsehraRenewalTasks
      case ProductIds.IchraId:
        return IchraRenewalTasks
      default:
        return PerksRenewalTasks
    }
  })()

  const resolveAssignEmployeesForProduct = () => {
    if (ProductIds.GroupHraId === product.productId) {
      return AssignEmployeeClasses
    }
    return AssignPerksEmployeeClasses
  }

  const dismissEnhanceBenefitNotifications = async () => {
    const result = await sendSaveCompanyUserNotificationAction(
      notifications.find((x) => x.notificationType === NotificationType.PopUpForPremiumPlus).notificationId,
    )
    if (result.completed) {
      await reloadShowPremiumPlus()
    } else if (result.errors.length > 0) {
      Notification.open({
        message: result.errors[0],
        type: 'error',
      })
    }
  }

  const handleEnablePremiumAnswer = () => {
    setShowSelectReimbursementProductModal(true)
  }

  const onSelectReimbursementProductSubmit = async (reimbursementProduct: number) => {
    const result = await sendCommand(() =>
      updateReimbursementProduct({
        reimbursementProduct: reimbursementProduct,
        productClassId: showPremiumPlusModals.productClassId,
        notificationId: notifications.find((x) => x.notificationType === NotificationType.PopUpForPremiumPlus)
          .notificationId,
      }),
    )

    if (result.errors.length <= 0) {
      await reloadShowPremiumPlus()
      Notification.open({
        message: 'Successful',
        type: 'success',
      })
      setShowSelectReimbursementProductModal(false)
    } else {
      Notification.open({
        message: result.errors[0],
        type: 'error',
      })
    }
  }

  const onSelectReimbursementProductCancel = async () => {
    setShowEnhanceBenefitModal(false)
    setShowSelectReimbursementProductModal(false)
  }

  return (
    <AdminDashboardWrapper dependantTasks={tasks} permissions={user.permissions} user={user}>
      <Switch>
        <Route
          key={ROUTE_PATHS.ADMIN_PROFILE}
          path={ROUTE_PATHS.ADMIN_PROFILE}
          component={ProductIds.GroupHraId === product.productId ? MyProfileGroupHra : MyProfilePerks}
        />
        <Route
          key={ROUTE_PATHS.ADMIN_CHANGE_PASSWORD}
          path={ROUTE_PATHS.ADMIN_CHANGE_PASSWORD}
          component={ChangePassword}
        />
        <Route key={ROUTE_PATHS.RENEWAL_TASKS} path={ROUTE_PATHS.RENEWAL_TASKS} component={resolveRenewalTasks} />
        <Route
          key={ROUTE_PATHS.ASSIGN_CLASSES}
          path={ROUTE_PATHS.ASSIGN_CLASSES}
          component={resolveAssignEmployeesForProduct()}
        />

        {navigationMenu.map((item: any) =>
          item.children.length > 0 ? getChildrenRoutes(item.children) : getRoute(item),
        )}
        <Route>
          <NotFound />
        </Route>
      </Switch>
      {!isShowPremiumModalsLoading &&
        showPremiumPlusModals &&
        showEnhanceBenefitModal &&
        !showSelectReimbursementProductModal && (
          <EnhanceBenefitModal
            isEnhanceBenefitModalOpen={showPremiumPlusModals.showPopup}
            handleMaybeLaterAnswer={dismissEnhanceBenefitNotifications}
            handleEnablePremiumAnswer={handleEnablePremiumAnswer}
            handleEnhanceBenefitModalClose={() => setShowEnhanceBenefitModal(false)}
          />
        )}
      {showSelectReimbursementProductModal && (
        <SelectReimbursementProductModal
          isSelectReimbursementProductModalOpen={showSelectReimbursementProductModal}
          onSubmit={onSelectReimbursementProductSubmit}
          onCancel={onSelectReimbursementProductCancel}
        />
      )}
    </AdminDashboardWrapper>
  )
}

export default AdminDashboard
